import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { DeleteOutlined, ExportOutlined, FileTextOutlined, HistoryOutlined, PictureOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import MainLayout from 'layouts/MainLayout';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { findFirstAvailableMenuItem } from 'utils/menuUtils';
import { WEBSITE_CONTENT_SITE_URL } from './graphql/queries';

/** 官网内容模块外框：一级菜单「官网内容」，二级菜单为内容列表、媒体库、已删除、操作日志 */
const WebsiteContentLayout: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const canManage = hasResource([ResourceCode.websiteContent.MANAGE]);
    // 媒体库：内容管理或媒体库资源码任一即可查看与上传，删除只限媒体库资源码
    const canUseMedia = hasResource([ResourceCode.websiteContent.MANAGE, ResourceCode.websiteContent.MEDIA_MANAGE]);
    const canViewLogs = hasResource([ResourceCode.websiteContent.MANAGE, ResourceCode.websiteContent.LOG_ALL]);
    // 「查看官网」入口（Q26）：官网地址按环境配置，由后端返回
    const { data: siteData } = useQuery<{ websiteContentSiteUrl: string }>(WEBSITE_CONTENT_SITE_URL, {
        skip: !canUseMedia,
        fetchPolicy: 'cache-first',
    });
    const siteUrl = siteData?.websiteContentSiteUrl;

    const menuItems = [
        {
            key: 'list',
            label: t('websiteContent.menu.list'),
            icon: <FileTextOutlined />,
            display: canManage,
            onClick: () => navigate('/website-content/list'),
        },
        {
            key: 'media',
            label: t('websiteContent.menu.media'),
            icon: <PictureOutlined />,
            display: canUseMedia,
            onClick: () => navigate('/website-content/media'),
        },
        {
            key: 'deleted',
            label: t('websiteContent.menu.deleted'),
            icon: <DeleteOutlined />,
            display: canManage,
            onClick: () => navigate('/website-content/deleted'),
        },
        {
            key: 'logs',
            label: t('websiteContent.menu.logs'),
            icon: <HistoryOutlined />,
            display: canViewLogs,
            onClick: () => navigate('/website-content/logs'),
        },
    ];

    useEffect(() => {
        const isRoot = location.pathname === '/website-content' || location.pathname === '/website-content/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <MainLayout
            activeFirstMenu="website-content"
            secondMenu={menuItems}
            secondFooter={siteUrl && (
                <Button block icon={<ExportOutlined />} href={siteUrl} target="_blank" rel="noopener noreferrer">
                    {t('websiteContent.menu.viewSite')}
                </Button>
            )}
        >
            <div className="p-6 h-full flex flex-col">
                <Outlet />
            </div>
        </MainLayout>
    );
};

export default WebsiteContentLayout;
